// <lang-switcher> Web Component
//
// PR-i18n.1 of the i18n architecture follow-up
// (memory/i18n_architecture_plan.md v3 §1.2, §6). Replaces the legacy
// `setLanguage(code) → location.reload()` flow with a no-reload path:
// writes cookie + localStorage, dispatches 'lang-change' event, all
// <localize-text> elements re-render. Same shape as
// <currency-switcher> (PR-B2).
//
// Three additional responsibilities beyond <currency-switcher>:
// 1. Update `document.documentElement.lang` so CSS :lang() pseudo-class
//    + screen readers see the new locale.
// 2. Set `document.documentElement.dir = 'rtl'` for ar/he/fa/ur, 'ltr'
//    otherwise. Same RTL set the SSR `<html dir>` would have used
//    pre-Phase-3 (verified in static/templates/pages/home.html:3 etc.).
// 3. Swap the trigger flag image src using the languages metadata
//    exposed as window.i18nLangs by base.html.
//
// SSR markup (emitted by base.html — to be wired by PR-i18n.1's
// base.html edits):
//   <lang-switcher class="relative" x-data="{...}">
//     <button>
//       <img data-lang-trigger-flag src="...">
//       <span data-lang-trigger-label>{{.Lang}}</span>
//     </button>
//     <div>...dropdown...
//       <button data-lang-code="fr">...French...</button>
//       <button data-lang-code="de">...German...</button>
//     </div>
//   </lang-switcher>
//
// PR-i18n.3 wires the new element into the SSR templates AND removes
// the legacy setLanguage() reload path. PR-i18n.1 just defines the
// element.

(function () {
    const RTL_BASES = { ar: true, he: true, fa: true, ur: true };

    function _isRTL(lang) {
        if (!lang) return false;
        const base = lang.toLowerCase().split('-')[0];
        return !!RTL_BASES[base];
    }

    function _flagFor(code) {
        const langs = window.i18nLangs || [];
        for (let i = 0; i < langs.length; i++) {
            if (langs[i].code === code || langs[i].code === code.toLowerCase()) {
                return langs[i].flag;
            }
        }
        return '';
    }

    // Sets <html lang="…" dir="…">. Note that setting dir="rtl" is
    // NECESSARY but NOT SUFFICIENT for an Arabic/Hebrew user to see a
    // correctly-mirrored UI — the browser only flips text-flow + a
    // handful of native widget defaults. Layout (margins, padding,
    // icon positions, alignment) is controlled by Tailwind classes,
    // and the templates use ~138 physical-direction classes (ml-,
    // mr-, pl-, pr-, text-left, text-right, etc.) vs only ~24 logical
    // ones (ms-, me-, ps-, pe-, text-start). Physical classes do NOT
    // flip with dir="rtl".
    //
    // Net effect today: Arabic content renders right-to-left within
    // text blocks (browser default), but the surrounding layout stays
    // LTR-shaped. Looks ~80% wrong by booking.com's standard.
    //
    // To actually mirror the UI, future work needs ONE of:
    //   1. rtl: variant overrides on physical classes (mechanical, ~138 sites)
    //   2. Migrate ml-/mr-/pl-/pr-/text-left/right → ms-/me-/ps-/pe-/text-start/end
    //   3. Tailwind logical-properties plugin + comprehensive Arabic visual QA
    //
    // Documented as deferred in memory/i18n_architecture_plan.md §11.
    // Visible the moment ar.json (or other RTL locale) ships — until
    // then, the only impact is the pre-existing setLanguage('ar') →
    // reload path having the same broken-mirror result, which has
    // always been true.
    function _applyHtmlLangDir(lang) {
        document.documentElement.lang = lang;
        document.documentElement.dir = _isRTL(lang) ? 'rtl' : 'ltr';
    }

    class LangSwitcher extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            const self = this;
            // PR-perf.12: capture-phase listener clones the dropdown's
            // <template data-lang-options> into the [data-lang-options-placeholder]
            // BEFORE Alpine's @click handler (bubble phase) toggles x-show.
            // Without capture-first, the dropdown would briefly render empty.
            // _ensureRendered is idempotent (gated on this._rendered).
            this.addEventListener('click', function () {
                self._ensureRendered();
            }, true);
            // Click delegation — survives any internal Alpine re-renders.
            // Wired synchronously because it only adds a listener on `this`;
            // no child queries.
            this.addEventListener('click', function (ev) {
                const btn = ev.target.closest('[data-lang-code]');
                if (!btn || !self.contains(btn)) return;
                const code = btn.getAttribute('data-lang-code');
                if (!code) return;
                ev.preventDefault();
                ev.stopPropagation();
                self._setLang(code);
            });
            // Per-element readyState-aware gate for first-paint sync.
            // connectedCallback for parser-inserted custom elements fires at
            // the OPENING tag, before children (flag <img>, dropdown buttons)
            // are parsed; querySelectorAll inside _updateTriggerFlag /
            // _updateTriggerLabel / _updateActiveHighlight would return empty
            // → silently broken first paint (memory/hydration_race_fixes_plan.md
            // §3.A). Defer to DOMContentLoaded when document is still loading;
            // run immediately for dynamic post-DCL insertions (innerHTML
            // synchronously inserts children, so they're present at upgrade).
            const run = function () { self._firstPaintSync(); };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        _firstPaintSync() {
            const stored = (function () {
                try { return localStorage.getItem('preferred_language') || ''; } catch (e) { return ''; }
            })();
            const cookie = (function () {
                const m = document.cookie.match(/(?:^|;\s*)preferred_language=([^;]+)/);
                return m ? decodeURIComponent(m[1]) : '';
            })();
            const cur = (stored || cookie || 'en-us').toLowerCase();
            if (cur !== 'en-us') {
                _applyHtmlLangDir(cur);
                this._updateTriggerFlag(cur);
                this._updateTriggerLabel(cur);
                this._updateActiveHighlight(cur);
            }
        }

        _setLang(code) {
            const lang = code.toLowerCase();
            // Pre-Cloudflare L1: Secure only on HTTPS (dev http localhost OK).
            const secure = location.protocol === 'https:' ? ';Secure' : '';
            try {
                document.cookie = 'preferred_language=' + encodeURIComponent(lang) + ';path=/;max-age=31536000;SameSite=Lax' + secure;
            } catch (e) { /* ignore */ }
            try { localStorage.setItem('preferred_language', lang); } catch (e) { /* ignore */ }

            _applyHtmlLangDir(lang);
            // Update ALL <lang-switcher> instances on the page so the
            // desktop and mobile switchers stay in sync (a click on
            // mobile should also flip the desktop trigger flag, in
            // case the user resizes back to desktop without
            // reloading). Per-instance update only would let one
            // switcher show a stale flag/highlight.
            document.querySelectorAll('lang-switcher').forEach(function (el) {
                if (typeof el._updateTriggerFlag === 'function') el._updateTriggerFlag(lang);
                if (typeof el._updateTriggerLabel === 'function') el._updateTriggerLabel(lang);
                if (typeof el._updateActiveHighlight === 'function') el._updateActiveHighlight(lang);
            });

            // Tell every <localize-text> on the page to re-evaluate.
            document.dispatchEvent(new CustomEvent('lang-change', {
                bubbles: true,
                detail: { lang: lang },
            }));

            // Close any open Alpine dropdown across the whole page.
            // Desktop's `languageOpen` lives on the header element's
            // x-data scope; mobile's `mobileLanguageOpen` lives on
            // the <lang-switcher>'s own x-data scope (template defines
            // it there to keep mobile state separate). Walk:
            //  - the header (desktop dropdown owner)
            //  - every <lang-switcher> instance (mobile + any future
            //    placement that adds its own scope; matches the
            //    "update all instances" pattern used for flag/label/
            //    highlight sync above so a viewport-resize edge case
            //    can't leave a hidden instance still flagged-open).
            const seen = new Set();
            const visit = function (el) {
                if (!el || seen.has(el)) return;
                seen.add(el);
                if (!el._x_dataStack || !el._x_dataStack[0]) return;
                const scope = el._x_dataStack[0];
                if ('languageOpen' in scope) scope.languageOpen = false;
                if ('mobileLanguageOpen' in scope) scope.mobileLanguageOpen = false;
            };
            visit(document.querySelector('header'));
            document.querySelectorAll('lang-switcher').forEach(visit);
        }

        _updateTriggerFlag(lang) {
            const flag = _flagFor(lang);
            if (!flag) return;
            this.querySelectorAll('[data-lang-trigger-flag]').forEach(function (img) {
                img.setAttribute('src', flag);
            });
        }

        _updateTriggerLabel(lang) {
            // Optional — most lang pickers show the flag, not the code.
            // Provided for parity with <currency-switcher>'s
            // [data-currency-trigger-label].
            this.querySelectorAll('[data-lang-trigger-label]').forEach(function (el) {
                el.textContent = lang;
            });
        }

        _updateActiveHighlight(lang) {
            this.querySelectorAll('[data-lang-code]').forEach(function (b) {
                const isActive = b.getAttribute('data-lang-code') === lang;
                if (isActive) {
                    b.classList.add('bg-blue-50', 'font-semibold', 'text-blue-600');
                } else {
                    b.classList.remove('bg-blue-50', 'font-semibold', 'text-blue-600');
                }
            });
        }

        // PR-perf.12: clones the <template data-lang-options> content into the
        // placeholder marker on first dropdown open. Idempotent. After cloning,
        // re-syncs the active-highlight to the visitor's actual preferred-language
        // (which may differ from the SSR-baked active class if they're a repeat
        // visitor with localStorage/cookie set — audit Plan M3 fold).
        _ensureRendered() {
            if (this._rendered) return;
            const tmpl = this.querySelector('template[data-lang-options]');
            const placeholder = this.querySelector('[data-lang-options-placeholder]');
            if (!tmpl || !placeholder) return; // SSR markup without lazy wrap; nothing to do.
            this._rendered = true;
            placeholder.replaceWith(tmpl.content.cloneNode(true));
            this._updateActiveHighlight(this._currentLang());
        }

        _currentLang() {
            let cur = '';
            try { cur = localStorage.getItem('preferred_language') || ''; } catch (e) { /* ignore */ }
            if (!cur) {
                const m = document.cookie.match(/(?:^|;\s*)preferred_language=([^;]+)/);
                cur = m ? decodeURIComponent(m[1]) : '';
            }
            return (cur || 'en-us').toLowerCase();
        }
    }

    if (!customElements.get('lang-switcher')) {
        customElements.define('lang-switcher', LangSwitcher);
    }

    // Cross-tab sync: when another tab sets preferred_language, propagate
    // here without writing back. Pre-Cloudflare M5. Symmetric to
    // currency-switcher's storage handler. <localize-text> elements re-render
    // on the lang-change event.
    window.addEventListener('storage', function (e) {
        if (e.key !== 'preferred_language' || !e.newValue) return;
        const lang = e.newValue.toLowerCase();
        _applyHtmlLangDir(lang);
        document.querySelectorAll('lang-switcher').forEach(function (el) {
            if (typeof el._updateTriggerFlag === 'function') el._updateTriggerFlag(lang);
            if (typeof el._updateTriggerLabel === 'function') el._updateTriggerLabel(lang);
            if (typeof el._updateActiveHighlight === 'function') el._updateActiveHighlight(lang);
        });
        document.dispatchEvent(new CustomEvent('lang-change', {
            bubbles: true,
            detail: { lang: lang },
        }));
    });
})();
